import { useEffect, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import {
  nomeDispositivo, nomeVerifica, passkeyDisponibile, segnaComputerRicordato, suComputer,
} from '../servizi/accesso-ricordato.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Avviso } from './Pezzi.tsx'
import { Bottone, Riquadro } from './base.tsx'
import { useRicordaComputer } from './RicordaComputer.tsx'
import { giorno, type Dispositivo } from './TelefoniConnessi.tsx'

/**
 * «Computer ricordati» in «Il mio profilo» (531, decisioni di Davide del 2/11/2026): i computer che entrano
 * con Windows Hello o Touch ID senza password, con il nome del browser, l'ultimo uso e «Revoca», che chiude
 * anche le sessioni aperte da quel computer. Da un computer non ancora ricordato, con una passkey della
 * piattaforma, c'e' anche «Ricorda questo computer»: la proposta dopo l'accesso si puo' rimandare. Il
 * riquadro non compare se non c'e' niente da mostrare ne' da fare, e impersonando (524) non ricorda niente.
 */
export function ComputerRicordati() {
  const { io, aggiorna } = useSessione()
  const [computer, setComputer] = useState<Dispositivo[]>([])
  const [disponibile, setDisponibile] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const impersonando = !!io?.impersonazione

  const carica = () => api.get<Dispositivo[]>('/auth/telefoni')
    .then((t) => setComputer(t.filter((x) => x.tipo === 'computer')))
  useEffect(() => { carica().catch(() => setComputer([])) }, [])
  useEffect(() => {
    if (!suComputer() || impersonando) return
    let vivo = true
    void passkeyDisponibile().then((si) => { if (vivo) setDisponibile(si) })
    return () => { vivo = false }
  }, [impersonando])

  const ricordo = useRicordaComputer(() => { void carica(); void aggiorna() })

  async function revoca(c: Dispositivo) {
    setErrore(null); setInCorso(c.id)
    try {
      await api.post(`/auth/telefoni/${c.id}/revoca`)
      if (c.questo) {
        // Revocando il computer in uso si chiude anche la sessione, se l'ha aperta lui.
        segnaComputerRicordato(false)
        await aggiorna()
      }
      await carica()
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata: riprova.')
    } finally {
      setInCorso(null)
    }
  }

  const puoRicordare = disponibile && !impersonando && !computer.some((c) => c.questo)
  if (!computer.length && !puoRicordare) return null
  return (
    <Riquadro titolo="Computer ricordati">
      <p className="piccolo tenue">
        Da questi computer entri con Windows Hello o Touch ID, senza password né codice del secondo fattore, per
        30 giorni dall’ultimo uso; la sessione resta aperta 4 ore senza attività. Ricorda solo i computer tuoi, non
        quelli condivisi. Se ne perdi uno o lo lasci, revocalo da qui.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {computer.length > 0 && (
        <ul className="telefoni-connessi">
          {computer.map((c) => (
            <li key={c.id}>
              <div>
                <strong>{nomeDispositivo(c.etichetta) ?? 'Computer'}</strong>
                {c.questo && <span className="piccolo tenue"> · questo computer</span>}
                <div className="piccolo tenue">
                  Ricordato il {giorno(c.creato_il)}
                  {c.usato_il ? `, ultimo uso il ${giorno(c.usato_il)}` : ''}, vale fino al {giorno(c.scade_il)}
                </div>
              </div>
              <Bottone variante="secondario" dimensione="piccolo" inCorso={inCorso === c.id}
                       onClick={() => { void revoca(c) }}>
                Revoca
              </Bottone>
            </li>
          ))}
        </ul>
      )}
      {puoRicordare && (
        <div className="computer-ricordati-azione">
          {ricordo.errore && <Avviso tipo="errore">{ricordo.errore}</Avviso>}
          <Bottone variante="secondario" inCorso={ricordo.inCorso} onClick={() => { void ricordo.ricorda() }}>
            Ricorda questo computer con {nomeVerifica()}
          </Bottone>
        </div>
      )}
    </Riquadro>
  )
}
